import React, { useEffect, useReducer, useState } from "react";
import Select from "react-select";
import Button from "react-bootstrap/Button";
import Table from "react-bootstrap/Table";
import Axios from 'axios';
import AxiosConfig from '../../utilities/AxiosConfig';
import Loader from '../../components/common/Loader';
import { connect } from 'react-redux'
import dayjs from "dayjs";
import "./Delegation.css";

// RESPONSE INTERFACES -------------------------------
interface DelegationType {
    ResponsibilityDelegationTypeId: number;
    Type: string;
};
interface DelegateOption {
    EmployeeID: number;
    FirstName: string;
    MiddleInit: string;
    LastName: string;
    ProfitCenterID: number;
    ProfitCenterLongDesc: string;
};
// ---------------------------------------------------

// LOADING INTERFACES --------------------------------
interface LoadingAction {
    type: string;
    payload: any;
};
interface DataLoadReducer {
    typeDataLoaded: boolean;
    delegateDataLoaded: boolean;
    responsibilityDelegationLoaded: boolean;
    loadingError: boolean;
};
// ---------------------------------------------------

// SELECT ELEMENT INTERFACES -------------------------
interface SelectOption {
    value: string;
    label: string;
};
// ---------------------------------------------------
interface Responsibilities {
    DelegationId: number;
    DelegationTypeId: number;
};

interface DelegationTableRow {
    StartDate: string;
    EndDate: string;
    EmployeeId: number;
    DelegateEmployeeId: number;
    DelegateLabel: string;
    Responsibilities: Responsibilities[];
    TypeLabel: string | undefined;
    CreatedDate: string;
    CreatedBy: string;
    ModifiedDate: string | undefined;
    ModifiedBy: string | undefined;
    DeletedDate: string | undefined;
    DeletedBy: string | undefined;
    DisabledDate: string | undefined;
    DisabledBy: string | undefined;
};

interface ResponsibilityDelegationPayload {
    ResponsibilityDelegationId: number;
    ResponsibilityDelegationTypeId: number;
    EmployeeId: number;
    DelegateEmployeeId: number;
    StartDateUtc: string;       // what happens when a string is passed in to this field on the backend? Does it get converted or changed?
    EndDateUtc: string;
    CreatedDateUtc: string;
    CreatedBy: string;  // can pass in '0' and backend will add in
    ModifyDateUtc?: string | undefined;
    ModifiedBy?: string | undefined;
    DeletedDateUtc?: string | undefined;
    DeletedBy?: string | undefined;
    DisabledDateUtc?: string | undefined;
    DisabledBy?: string | undefined;
};
// ---------------------------------------------------

// LOADING REDUCER -----------------------------------
const LOADING_ACTIONS = {
    typeDataLoaded: 'typeDataLodaded',
    delegateDataLoaded: 'delegateDataLoaded',
    responsibilityDelegationLoaded: 'responsibilityDelegationLoaded',
    loadingError: 'loadingError'
};

// false means that call is in progress and the page will show loading,
//  when all three data calls are showing true, the page will render
const loadingReducer = (loadingState: DataLoadReducer, action : LoadingAction): any => {
    switch (action.type) {
        case LOADING_ACTIONS.typeDataLoaded:
            return { ...loadingState, typeDataLoaded: action.payload }
        case LOADING_ACTIONS.delegateDataLoaded:
            return { ...loadingState, delegateDataLoaded: action.payload }        
        case LOADING_ACTIONS.responsibilityDelegationLoaded:
            return { ...loadingState, responsibilityDelegationLoaded: action.payload }     
        case LOADING_ACTIONS.loadingError:
            return { ...loadingState, loadingError: action.payload } 
        default:
            return loadingState
    }
};
// ---------------------------------------------------

const Delegation = (props: any) => {    
    const userId = props.userId;
        
// USESTATES -----------------------------------------    
    // RESPONSE DATA
    const [typeResponse, setTypeResponse] = useState<DelegationType[]>([]);
    const [delegateResponse, setDelegateResponse] = useState<DelegateOption[]>([]);
    const [delegationResponse, setDelegationResponse] = useState<ResponsibilityDelegationPayload[]>([]);
    const[errorMessage, setErrorMessage] = useState<String | undefined>(undefined);
    // FORMATTED DATA
    const [delegationTypeSelectOptions, setDelegationTypeSelectOptions] = useState<SelectOption[]>([]);
    const [delegateSelectOptions, setDelegateSelectOptions] = useState<SelectOption[]>([]);
    const [delegationTableRows, setDelegationTableRows] = useState<DelegationTableRow[]>([]);
    // FIELD VALUES
    const [delegationStartDate, setDelegationStartDate] = useState<string>('');
    const [delegationEndDate, setDelegationEndDate] = useState<string>('');
    const [selectedDelegationTypes, setSelectedDelegationTypes] = useState<SelectOption[]>([]);
    const [selectedDelegate, setSelectedDelegate] = useState<SelectOption | null>(); 
// ----------------------------------------------------

// REDUCERS -------------------------------------------
    const initialLoadingState: DataLoadReducer = {
        typeDataLoaded: false,
        delegateDataLoaded: false,
        responsibilityDelegationLoaded: false,
        loadingError: false
    };

    const [loadingState, loadingDispatch] = useReducer(loadingReducer, initialLoadingState);
// ----------------------------------------------------

// API CALLS ------------------------------------------
    //
    const getDelegationTypes = () => {
        Axios.get(`${process.env.REACT_APP_API_URL}/api/delegation/getResponsibilityDelegationTypes`, AxiosConfig.configValue())
        // Axios.get(`http://localhost:3001/api/delegation/getResponsibilityDelegationTypes`, AxiosConfig.configValue())
        .then(response => {
            const tempDelegationTypes: DelegationType[] = [];
            response.data.forEach((type: DelegationType) => {
                tempDelegationTypes.push({ ResponsibilityDelegationTypeId: type.ResponsibilityDelegationTypeId, Type: type.Type });
            });
            setTypeResponse(tempDelegationTypes);
            loadingDispatch({ type: LOADING_ACTIONS.typeDataLoaded, payload: true });        
        }).catch(error => {
            loadingDispatch({ type: LOADING_ACTIONS.loadingError, payload: true });
        }).finally(() => { });
    };

    //
    const getDelegateOptions = () => { 
        Axios.get(`${process.env.REACT_APP_API_URL}/api/delegation/getResponsibilityDelegateOptions`, AxiosConfig.configValue())
        // Axios.get(`http://localhost:3001/api/delegation/getResponsibilityDelegateOptions`, AxiosConfig.configValue())
        .then(response => {            
            const tempDelegates: DelegateOption[] = [];
            response.data.forEach((delegate: DelegateOption) => {
                tempDelegates.push({
                    EmployeeID: delegate.EmployeeID,
                    FirstName: delegate.FirstName,
                    MiddleInit: delegate.MiddleInit,
                    LastName: delegate.LastName,
                    ProfitCenterID: delegate.ProfitCenterID,
                    ProfitCenterLongDesc: delegate.ProfitCenterLongDesc
                });
            });
            setDelegateResponse(tempDelegates);
            loadingDispatch({ type: LOADING_ACTIONS.delegateDataLoaded, payload: true });            
        }).catch(error => {
            loadingDispatch({ type: LOADING_ACTIONS.loadingError, payload: true });
        }).finally(() => { });
    };

    //
    const getDelegationByDelegateEmployeeID = () => {
        loadingDispatch({ type: LOADING_ACTIONS.responsibilityDelegationLoaded, payload: false });

        Axios.get(`${process.env.REACT_APP_API_URL}/api/delegation/getResponsibilityDelegationByDelegateEmployeeID`, AxiosConfig.configValue())
        // Axios.get(`http://localhost:3001/api/delegation/getResponsibilityDelegationByDelegateEmployeeID`, AxiosConfig.configValue())
        .then(response => {            
            const tempDelegationData: ResponsibilityDelegationPayload[] = [];
            response.data.forEach((delegation: ResponsibilityDelegationPayload) => {
                tempDelegationData.push({
                    ResponsibilityDelegationId: delegation.ResponsibilityDelegationId,
                    ResponsibilityDelegationTypeId: delegation.ResponsibilityDelegationTypeId,
                    EmployeeId: delegation.EmployeeId,
                    DelegateEmployeeId: delegation.DelegateEmployeeId,
                    StartDateUtc: delegation.StartDateUtc,
                    EndDateUtc: delegation.EndDateUtc,
                    CreatedDateUtc: delegation.CreatedDateUtc,
                    CreatedBy: delegation.CreatedBy,
                    ModifyDateUtc: delegation.ModifyDateUtc,
                    ModifiedBy: delegation.ModifiedBy,
                    DeletedDateUtc: delegation.DeletedDateUtc,
                    DeletedBy: delegation.DeletedBy,
                    DisabledDateUtc: delegation.DisabledDateUtc,
                    DisabledBy: delegation.DisabledBy
                });
            });
            setDelegationResponse(tempDelegationData);
            loadingDispatch({ type: LOADING_ACTIONS.responsibilityDelegationLoaded, payload: true });
        }).catch(error => {
            loadingDispatch({ type: LOADING_ACTIONS.loadingError, payload: true });
        }).finally(() => { });
    };
    
    //
    const addNewDelegation = () => {
        selectedDelegationTypes.forEach((delegationType) => {
            const payload: ResponsibilityDelegationPayload = {
                ResponsibilityDelegationId: -1, // null
                ResponsibilityDelegationTypeId: Number(delegationType.value),
                EmployeeId: Number(userId),
                DelegateEmployeeId: Number(selectedDelegate?.value),
                StartDateUtc: `${dayjs(delegationStartDate).hour(0).minute(0).second(0).format('YYYY-MM-DDTHH:mm:ss')}`,
                EndDateUtc: `${dayjs(delegationEndDate).hour(23).minute(59).second(59).format('YYYY-MM-DDTHH:mm:ss')}`,
                CreatedDateUtc: `${dayjs().format('YYYY-MM-DDTHH:mm:ss')}`,
                CreatedBy: '0'
            };

            Axios.post(`${process.env.REACT_APP_API_URL}/api/delegation/addOrUpdateResponsibilityDelegation`,
            // Axios.post(`http://localhost:3001/api/delegation/addOrUpdateResponsibilityDelegation`,
            payload,
            AxiosConfig.configValue('min'))
            .then(response => {
                getDelegationByDelegateEmployeeID();
            }).catch(error => {
            }).finally(() => { });
        });
    };

    //
    const deleteExistingDelegation = (row: DelegationTableRow) => {
        row.Responsibilities.forEach((delegation) => {            
            const payload: ResponsibilityDelegationPayload = {
                ResponsibilityDelegationId: delegation.DelegationId,
                ResponsibilityDelegationTypeId: delegation.DelegationTypeId,
                EmployeeId: row.EmployeeId,
                DelegateEmployeeId: row.DelegateEmployeeId,
                StartDateUtc: row.StartDate,
                EndDateUtc: row.EndDate,
                CreatedDateUtc: row.CreatedDate,
                CreatedBy: `${row.CreatedBy}`,
                ModifyDateUtc: dayjs().format('YYYY-MM_DDTHH:mm:ss'),
                ModifiedBy: userId,
                DeletedDateUtc: dayjs().format('YYYY-MM-DDTHH:mm:ss'),
                DeletedBy: userId,
                DisabledDateUtc: row.DisabledDate,
                DisabledBy: row.DisabledBy
            };

            Axios.post(`${process.env.REACT_APP_API_URL}/api/delegation/addOrUpdateResponsibilityDelegation`,
            // Axios.post(`http://localhost:3001/api/delegation/addOrUpdateResponsibilityDelegation`,
            payload,
            AxiosConfig.configValue('min'))
            .then(response => {
                getDelegationByDelegateEmployeeID();
            }).catch(error => {
            }).finally(() => { });
        });
    };
// ----------------------------------------------------

// FUNCTIONS ------------------------------------------
    const isLoading = () => {
        if (loadingState.typeDataLoaded === false || loadingState.delegateDataLoaded === false || loadingState.responsibilityDelegationLoaded === false) {
            return true;
        }
        return false;
    };

    const clearDelegationFields = () => {
        setDelegationStartDate('');
        setDelegationEndDate('');
        setSelectedDelegationTypes([]);
        setSelectedDelegate(null);
        setErrorMessage(undefined);
    };

    // build the `type` string for combined rows in the delegation table from an array of delegationTypeID's
    const getTypeLabel = (typeIdList: number[]) => {
        let label = ''
        typeIdList.forEach((id) => {
            const type = typeResponse.find((type) => type.ResponsibilityDelegationTypeId === id);
            if (type == undefined) {
                // TODO: handle useCase where type doesn't exist in response data
            }

            if (label.length === 0) {
                label = type?.Type ? type?.Type.toString() : '';
            } else {
                label = label + '/' + (type?.Type ? type?.Type.toString() : '');
            }
        });

        return label;
    };

    const getDelegateLabel = (delegateEmployeeId: number) => {
        const delegate = delegateResponse.find((delegate) => delegate.EmployeeID === delegateEmployeeId);
        if (delegate == undefined) {
            // TODO: handle useCase where delegate doesn't exist in response data
        }

        let delegateLabel = '';
        if (delegate?.MiddleInit == undefined || delegate.MiddleInit === '') {
            delegateLabel = `${delegate?.FirstName} ${delegate?.LastName} (${delegate?.ProfitCenterLongDesc})`;
        } else {
            delegateLabel = `${delegate.FirstName} ${delegate.MiddleInit}. ${delegate.LastName} (${delegate.ProfitCenterLongDesc})`;
        }

        return delegateLabel;
    };

    const formatDelegationTableRows = () => {
        let tempDelegationData: DelegationTableRow[] = [];
        delegationResponse.forEach((delegation) => {                 
            // get index of delegation in tempDelegationData with same START DATE, END DATE, & DELEGATE EMPLOYEE ID
            const index = tempDelegationData.findIndex((i) => i.StartDate === delegation.StartDateUtc
                && i.EndDate === delegation.EndDateUtc && i.DelegateEmployeeId === delegation.DelegateEmployeeId
                && i.Responsibilities[0].DelegationId !== delegation.ResponsibilityDelegationId
            );
            
            /* if there is a similar delegation, add the DELEGATION ID & DELEGATION TYPE ID to the Responsibilities array 
            of that delegation, and change the label to reflect all delegation types */
            if (index >= 0) {
                tempDelegationData[index].Responsibilities.push({ DelegationId: delegation.ResponsibilityDelegationId, DelegationTypeId: delegation.ResponsibilityDelegationTypeId });
                const typesArray = tempDelegationData[index].Responsibilities.map((j) => j.DelegationTypeId);
                tempDelegationData[index].TypeLabel = getTypeLabel(typesArray);
                return;     // return so the delegation doesn't get it's own row in the table
            }
            
            const tempTableRow: DelegationTableRow = {
                StartDate: delegation.StartDateUtc,
                EndDate: delegation.EndDateUtc,
                EmployeeId: delegation.EmployeeId,
                DelegateEmployeeId: delegation.DelegateEmployeeId,
                DelegateLabel: getDelegateLabel(delegation.DelegateEmployeeId),
                Responsibilities: [{ DelegationId: delegation.ResponsibilityDelegationId, DelegationTypeId: delegation.ResponsibilityDelegationTypeId }],
                TypeLabel: getTypeLabel([ delegation.ResponsibilityDelegationTypeId ]),
                CreatedDate: delegation.CreatedDateUtc,
                CreatedBy: delegation.CreatedBy,
                ModifiedDate: delegation.ModifyDateUtc,
                ModifiedBy: delegation.ModifiedBy,
                DeletedDate: delegation.DeletedDateUtc,
                DeletedBy: delegation.DeletedBy,
                DisabledDate: delegation.DisabledDateUtc,
                DisabledBy: delegation.DisabledBy
            };
            tempDelegationData.push(tempTableRow);
        });
        
        setDelegationTableRows(tempDelegationData);
    };

    const validateNewDelegationFields = () => {
        // =============== CHECK THAT FIELDS HAVE VALUES =================
        if (delegationStartDate == null || delegationEndDate == null || delegationStartDate == '' || delegationEndDate == '') {
            setErrorMessage('A valid start/end date is required to add a new delegation.');
            return;
        }
        if (selectedDelegationTypes.length === 0) {
            setErrorMessage('A delegation type is required to add a new delegation.');
            return;
        }
        if (selectedDelegate == null) {
            setErrorMessage('A delegate must be selected to add a new delegation.');
            return;
        }
        // ===============================================================

        const newStartDate = `${dayjs(delegationStartDate).format('YYYY/MM/DD')}`;
        const newEndDate = `${dayjs(delegationEndDate).format('YYYY/MM/DD')}`;
        // array of the selected delegation type ID's
        const newTypes = selectedDelegationTypes.map((i) => i.value); 

        // CHECK FOR DATES IN THE PAST
        if (newStartDate < dayjs().format('YYYY/MM/DD') || newEndDate < dayjs().format('YYYY/MM/DD')) {
            setErrorMessage('The selected dates cannot be in the past.');
            return;
        }

        // MAKE SURE DATES ARE IN CORRECT ORDER
        if (newEndDate < newStartDate) {
            setErrorMessage('The end delegation date needs to be on/after the start delegation date.');
            return;
        }

        // ======== CHECK THAT DATE-RANGE/TYPE COMBO DOESN'T EXIST ==========
        let dateRangeTypeConflict = false;
        const sameDateRangeDelegations: ResponsibilityDelegationPayload[] = delegationResponse.filter((t) =>
            newStartDate === `${dayjs(t.StartDateUtc).format('YYYY/MM/DD')}` && newEndDate === `${dayjs(t.EndDateUtc).format('YYYY/MM/DD')}`);
        for (let i = 0; i < sameDateRangeDelegations.length; i++) {
            if (newTypes.includes(`${sameDateRangeDelegations[i].ResponsibilityDelegationTypeId}`)) {
                dateRangeTypeConflict = true;
                break;
            }
        }
        if (dateRangeTypeConflict) {
            setErrorMessage('There is already a delegation for that date range and type.');
            return;
        }
        // ==================================================================

        let datesConflict = false;
        // verify that there isn't already a delegate within this date range for this delegation type
        if (!dateRangeTypeConflict) {
            for (let i = 0; i < delegationResponse.length; i++) {
                const existingStartDate = `${dayjs(delegationResponse[i].StartDateUtc).format('YYYY/MM/DD')}`;
                const existingEndDate = `${dayjs(delegationResponse[i].EndDateUtc).format('YYYY/MM/DD')}`;            
    
                if (((newStartDate >= existingStartDate && newStartDate <= existingEndDate)
                    || (existingStartDate >= newStartDate && existingStartDate <= newEndDate))
                    && newTypes.includes(`${delegationResponse[i].ResponsibilityDelegationTypeId}`)
                ) {
                    // the chosen dates conflict with existing delegation
                    setErrorMessage('There is a conflict of dates for this delegation type.');
                    datesConflict = true;
                    break;
                } 
            }
        }

        if (!datesConflict) {
            addNewDelegation();
            clearDelegationFields();
        }
    };   
// ----------------------------------------------------

// FORMATTERS -----------------------------------------    
    //
    const formatTypeSelectOptions = () => {
        const tempSelectOptions: SelectOption[] = [];
        typeResponse.forEach((type: DelegationType) => {
            tempSelectOptions.push({ value: type.ResponsibilityDelegationTypeId.toString(), label: type.Type })
        });
        setDelegationTypeSelectOptions(tempSelectOptions);
    };

    //
    const formatDelegateSelectOptions = () => {
        const tempSelectOptions: SelectOption[] = [];
        delegateResponse.forEach((delegate: DelegateOption) => {
            let delegateLabel = '';
            if (delegate.MiddleInit == undefined || delegate.MiddleInit === '') {
                delegateLabel = `${delegate.FirstName} ${delegate.LastName} (${delegate.ProfitCenterLongDesc})`;
            } else {
                delegateLabel = `${delegate.FirstName} ${delegate.MiddleInit}. ${delegate.LastName} (${delegate.ProfitCenterLongDesc})`;
            }
            tempSelectOptions.push({ value: delegate.EmployeeID.toString(), label: delegateLabel });
        });
        setDelegateSelectOptions(tempSelectOptions);        
    };
// ----------------------------------------------------

// RENDER FUNCTIONS -----------------------------------
    //
    const renderDelegationTable = () => {
        return (
            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Delegate</th>
                        <th>Type</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <>
                        {delegationTableRows.map((row) => (
                            <tr>
                                <td>{`${dayjs(row.StartDate).format('MM/DD/YYYY')}`}</td>
                                <td>{dayjs(row.EndDate).format('MM/DD/YYYY')}</td>
                                <td>{row.DelegateLabel}</td>
                                <td>{row.TypeLabel}</td>
                                <td><span className="material-icons" onClick={() => deleteExistingDelegation(row)}>delete</span></td>
                            </tr>
                        ))}
                    </>
                </tbody>
            </Table>
        );
    };
// ----------------------------------------------------

// USEEFFECTS -----------------------------------------
    useEffect(() => {
        getDelegationTypes();
        getDelegateOptions();
        getDelegationByDelegateEmployeeID();
    }, []);

    useEffect(() => {
        if (!isLoading()) {
            formatTypeSelectOptions();
            formatDelegateSelectOptions();
            formatDelegationTableRows();
        }
    }, [typeResponse, delegateResponse, delegationResponse]);
// ----------------------------------------------------    
    //
    const formatSelectedDelegationTypes = (selectedTypes: SelectOption[]) => {
        setSelectedDelegationTypes(selectedTypes);
    }

    //
    const formatSelectedDelegate = (selectedDelegate: SelectOption) => {
        setSelectedDelegate(selectedDelegate);
    };

    return (
        <div className="main-content delegation-container">
            {isLoading() && (loadingState.loadingError === false) && (
                <div>
                    <Loader title='Loading Delegation Data' />
                </div>
            )}
            {(loadingState.loadingError === true) && (
                <p>There was an error while fetching delegation data.</p>
            )}           
            {!isLoading() && (loadingState.loadingError === false) && (
                <>                
                    <div className="delegation-header-container">
                        <h2 className="delegation-header-title">Delegation</h2>
                    </div>
                    <div className="delegation-error-container">
                        {errorMessage == undefined && (
                            <p className="delegation-error-placeholder" />
                        )}
                        {errorMessage != undefined && (
                            <p className="delegation-error-message">{errorMessage}</p>
                        )}
                    </div>
                    <div className="delegation-body-container">
                        <div className="delegation-section-wrapper">
                            <div className="delegation-dropdown-container">
                                <div className="delegation-dropdown-wrapper">
                                    <div>Start Delegation Date:</div>
                                    <input type="date" className='delegation-date-picker' value={delegationStartDate} onChange={(e: any) => { setDelegationStartDate(e.target.value.toString()); setErrorMessage(undefined); }} min={dayjs().format('YYYY-MM-DD')} />
                                </div>
                                <div className="delegation-dropdown-wrapper">
                                    <div>End Delegation Date:</div>
                                    <input type="date" className='delegation-date-picker' value={delegationEndDate} onChange={(e: any) => { setDelegationEndDate(e.target.value.toString()); setErrorMessage(undefined); }} min={dayjs().format('YYYY-MM-DD')} />
                                </div>
                                <div className="delegation-dropdown-wrapper">
                                    <div>Delegation Type:</div>
                                    <Select isMulti options={delegationTypeSelectOptions} value={selectedDelegationTypes} onChange={(e: any) => { formatSelectedDelegationTypes(e); setErrorMessage(undefined); }} />
                                </div>
                                <div className="delegation-dropdown-wrapper">
                                    <div>Delegate:</div>
                                    <Select options={delegateSelectOptions} value={selectedDelegate} onChange={(e: any) => { formatSelectedDelegate(e); setErrorMessage(undefined); }} />
                                </div>
                            </div>
                            <div className="delegation-button-wrapper">
                                <Button className="delegation-button" variant="secondary" onClick={() => clearDelegationFields()}>Clear</Button>
                                <Button className="delegation-button" variant="primary" onClick={() => validateNewDelegationFields()}>Save</Button>
                            </div>                                                    
                        </div>
                        <hr />
                        <div className="delegation-section-wrapper">
                            {renderDelegationTable()}                            
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

const mapStateToProps = (state: any) => ({
    userId: state.user.userId
})

export default connect(mapStateToProps)(Delegation);